import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { lookupClient } from '@/lib/supabase-marketing';
import { scheduleIdleTask } from '@/lib/scheduleIdleTask';
import {
  DEFAULT_CLIENT_ID,
  writeClientData,
  readClientOverrideCookie,
  writeClientOverrideCookie,
} from '@/lib/client';

// Returns true once the client lookup has settled (resolved or degraded).
// Callers that need to gate on attribution (e.g. the Continue button on
// /plan-summary) use this to avoid writing the cart before the lookup finishes.
export function useClientTracking(): { resolved: boolean } {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolveClient = () => {
      const raw = searchParams.get('client')?.trim().slice(0, 128) || null;
      // An explicit ?client= always wins and refreshes the cookie; fall back to
      // a prior visit's cookie only when the URL has none, so a partner
      // override survives the perch-mobile round trip once sessionStorage is gone.
      const overrideId = raw || readClientOverrideCookie();
      const idToTry = overrideId || DEFAULT_CLIENT_ID;

      if (!idToTry) {
        setResolved(true);
        return;
      }

      lookupClient(idToTry).then((data) => {
        if (cancelled) return;
        if (data) {
          writeClientData(data);
          // Never persist DEFAULT_CLIENT_ID itself as an override.
          if (overrideId) writeClientOverrideCookie(overrideId);
          setResolved(true);
        } else if (overrideId && DEFAULT_CLIENT_ID) {
          lookupClient(DEFAULT_CLIENT_ID).then((fallback) => {
            if (cancelled) return;
            if (fallback) writeClientData(fallback);
            setResolved(true);
          });
        } else {
          setResolved(true);
        }
      });
    };

    const shouldResolveImmediately =
      location.pathname === '/plans' ||
      location.pathname === '/plan-summary' ||
      searchParams.has('client');
    const cancelIdleTask = shouldResolveImmediately
      ? undefined
      : scheduleIdleTask(resolveClient);

    if (shouldResolveImmediately) resolveClient();

    return () => {
      cancelled = true;
      cancelIdleTask?.();
    };
  }, [location.pathname, searchParams]);

  return { resolved };
}
